/**
 * PullToRefresh — PWA / "Add to Home Screen" pull-to-refresh gesture.
 *
 * Why this exists:
 *   When Flythouse is added to the iOS home screen (standalone / PWA mode),
 *   Safari strips its native browser chrome — including the built-in
 *   "pull down to refresh" gesture. Without this, users have to close
 *   the app and reopen it to see new content. This restores the
 *   familiar iOS pull-to-refresh interaction inside the installed PWA.
 *
 * Behavior:
 *   1. Only activates when the app is running in standalone (installed)
 *      mode. In a normal browser tab we do nothing — the browser's
 *      native gesture handles refresh better than any polyfill can.
 *   2. Only triggers when the page is scrolled to the very top
 *      (window.scrollY === 0) AND the user starts the drag from there.
 *   3. Tracks vertical finger travel with rubber-band easing.
 *   4. A translucent indicator (spinner glyph inside a glass pill)
 *      slides down from the top as the user pulls.
 *   5. Passing the ~72px threshold and releasing → window.location.reload().
 *      Under the threshold → snap back with no reload.
 *
 * Kept in one small self-contained file so it can be dropped in / out.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const THRESHOLD_PX = 72;      // finger travel needed to trigger refresh
const MAX_PULL_PX = 140;      // clamp so the indicator doesn't fly off
const RUBBER = 0.55;          // resistance factor — feels like iOS
const AXIS_LOCK_PX = 8;       // travel before we decide vertical vs horizontal
const AXIS_DOMINANCE = 1.4;   // dy must beat |dx| by this ratio to count as a pull

// Routes that opt into the pull-to-refresh gesture. Every other page
// (messages, stories, admin, etc.) needs its own scroll gestures left
// alone — pull-to-refresh here was intercepting legitimate upward
// scrolls in chat threads and other content-heavy views.
// iter199 · Added `/profile` (all personas — agent, athlete, brand,
// public user) so signed-in users can pull to refresh their card
// stack + roster/console just like they do on the home + network.
const REFRESH_ROUTES = new Set(['/', '/network', '/profile']);

// Detect PWA / installed mode. Covers iOS Safari (navigator.standalone),
// Chrome/Android (display-mode: standalone), and the Capacitor wrapper.
const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
    if (window.navigator && window.navigator.standalone === true) return true;
    // Capacitor sets window.Capacitor.isNativePlatform() for iOS/Android shells.
    if (window.Capacitor && typeof window.Capacitor.isNativePlatform === 'function') {
      return !!window.Capacitor.isNativePlatform();
    }
  } catch { /* private-mode / SSR / permissions weirdness */ }
  return false;
};

// iter301 · Detect the Capacitor native shell (iOS TestFlight / App
// Store build). Inside the native app the whole-page reload triggered
// by pull-to-refresh was resetting React state mid-render — freshly
// picked avatars vanished, the profile card flashed empty, and users
// reported having to "re-add" their picture. Per product decision the
// gesture is now DISABLED entirely on native iOS so the app never
// force-reloads out from under a user's edit.
const isNativeShell = () => {
  if (typeof window === 'undefined') return false;
  try {
    if (window.Capacitor && typeof window.Capacitor.isNativePlatform === 'function') {
      return !!window.Capacitor.isNativePlatform();
    }
  } catch { /* SSR / access errors — treat as web */ }
  return false;
};

const PullToRefresh = () => {
  const location = useLocation();
  const [installed, setInstalled] = useState(false);
  const [pull, setPull] = useState(0);      // current rubber-banded travel in px
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);              // touch-start clientY, or null if idle
  const startX = useRef(null);              // touch-start clientX — used for axis detection
  const lastY = useRef(0);
  const tracking = useRef(false);           // are we in a valid pull gesture?
  const axisDecided = useRef(false);        // have we locked in vertical intent?
  const abandoned = useRef(false);          // horizontal swipe detected — never engage this gesture

  useEffect(() => {
    setInstalled(isStandalone());
  }, []);

  // Only active on the network home routes. Everything else — messages,
  // stories, athlete pages, admin, portal — needs its own scroll
  // ownership. Recomputed on every route change.
  // iter213 · Removed the `installed` gate so the gesture also fires
  // in mobile Safari / Chrome tabs — the user asked for it back on
  // the homepage even when they haven't added the app to home screen.
  // `installed` is still tracked for potential future use (e.g. show
  // a subtler indicator when browser-native pull-to-refresh will also
  // fire), but no longer required to arm the handler.
  void installed;
  // iter301 · Hard-disable pull-to-refresh in the Capacitor native
  // shell. On iOS the `window.location.reload()` call was nuking React
  // state mid-flow (avatar upload, form drafts), so we suppress the
  // gesture entirely inside TestFlight / App Store builds. Users can
  // still refresh by killing + reopening the app.
  const enabled = REFRESH_ROUTES.has(location.pathname) && !isNativeShell();

  useEffect(() => {
    if (!enabled) return undefined;

    const onTouchStart = (e) => {
      // Only begin tracking when the page is at absolute top and
      // there's no open modal/scroller nearby taking the gesture.
      if (window.scrollY > 2) return;
      // iter200 · Bail if ANY modal / dialog is open. The FLYT ID™
      // builder + Athlete section editors have their OWN inner
      // scrollers; without this guard a scroll-up inside those
      // triggered the pull-to-refresh gesture on `/profile`.
      if (document.querySelector('[role="dialog"], [data-modal-open="true"], .fixed.inset-0')) return;
      if (!e.touches || e.touches.length !== 1) return;
      startY.current = e.touches[0].clientY;
      startX.current = e.touches[0].clientX;
      lastY.current = startY.current;
      tracking.current = true;
      axisDecided.current = false;
      abandoned.current = false;
    };

    const onTouchMove = (e) => {
      if (!tracking.current || abandoned.current || startY.current == null) return;
      const t = e.touches[0];
      const dy = t.clientY - startY.current;
      const dx = t.clientX - startX.current;
      lastY.current = t.clientY;

      // Axis lock — before travelling more than AXIS_LOCK_PX in any
      // direction we don't know if the user intends a pull-down or a
      // horizontal carousel swipe. Once we cross the lock threshold,
      // decide once and stick with it for the rest of this gesture.
      if (!axisDecided.current) {
        const absDx = Math.abs(dx);
        const absDy = Math.abs(dy);
        if (absDx < AXIS_LOCK_PX && absDy < AXIS_LOCK_PX) return;
        // If horizontal dominates OR the movement is going upward, bail
        // out for this whole gesture so the athlete carousel (or any
        // native scroller) keeps its swipe untouched.
        if (absDx >= absDy || dy <= 0 || absDy < absDx * AXIS_DOMINANCE) {
          abandoned.current = true;
          setPull(0);
          return;
        }
        axisDecided.current = true;
      }

      // Only respond to downward pulls at the very top of the page.
      if (dy <= 0 || window.scrollY > 2) {
        setPull(0);
        return;
      }
      // Rubber-banded translation — clamped so it never over-reaches.
      const eased = Math.min(MAX_PULL_PX, dy * RUBBER);
      setPull(eased);
      // Prevent the page from scrolling while we're intercepting.
      // (passive:false on the listener registration is required.)
      if (eased > 4) {
        try { e.preventDefault(); } catch { /* passive listener — ignore */ }
      }
    };

    const onTouchEnd = () => {
      if (!tracking.current) return;
      tracking.current = false;
      startY.current = null;
      startX.current = null;
      const wasAbandoned = abandoned.current;
      abandoned.current = false;
      axisDecided.current = false;
      if (wasAbandoned) { setPull(0); return; }
      const shouldRefresh = pull >= THRESHOLD_PX && !refreshing;
      if (shouldRefresh) {
        setRefreshing(true);
        // Hold the indicator visible briefly so the user sees the spinner
        // spin up, then reload — the reload obviously discards this
        // component so no need to reset state.
        setTimeout(() => {
          try { window.location.reload(); } catch { /* ignore */ }
        }, 350);
      } else {
        // Snap back with a tiny animation.
        setPull(0);
      }
    };

    // Passive: false on move so we can preventDefault when actively pulling.
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [enabled, pull, refreshing]);

  if (!enabled || (pull <= 0 && !refreshing)) return null;

  const armed = refreshing || pull >= THRESHOLD_PX;
  const opacity = refreshing ? 1 : Math.min(1, pull / THRESHOLD_PX);
  // Progress ring — degrees of the arc that's "filled".
  const deg = refreshing ? 360 : Math.min(360, (pull / THRESHOLD_PX) * 360);

  return (
    <div
      aria-hidden={!refreshing}
      role="status"
      className="pointer-events-none fixed inset-x-0 top-0 z-[200] flex justify-center"
      style={{ transform: `translateY(${refreshing ? 40 : Math.max(0, pull * 0.65)}px)`, transition: refreshing ? 'transform 200ms ease-out' : (tracking.current ? 'none' : 'transform 220ms cubic-bezier(0.22, 0.61, 0.36, 1)') }}
      data-testid="pull-to-refresh-indicator"
    >
      <div
        className="mt-2 w-11 h-11 rounded-full flex items-center justify-center"
        style={{
          background: 'rgba(0,0,0,0.7)',
          backdropFilter: 'blur(12px) saturate(180%)',
          WebkitBackdropFilter: 'blur(12px) saturate(180%)',
          border: '1px solid rgba(107,184,240,0.35)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.55), 0 0 20px rgba(107,184,240,0.25)',
          opacity,
        }}
      >
        {/* Progress arc that fills as the user pulls, then spins on refresh. */}
        <div
          className={refreshing ? 'animate-spin' : ''}
          style={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: `conic-gradient(rgba(107,184,240,${armed ? 1 : 0.9}) ${deg}deg, rgba(255,255,255,0.12) ${deg}deg)`,
            WebkitMask: 'radial-gradient(circle, transparent 55%, black 57%)',
            mask: 'radial-gradient(circle, transparent 55%, black 57%)',
          }}
        />
      </div>
    </div>
  );
};

export default PullToRefresh;
